import { Dialog, Transition } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Fragment, useCallback } from 'react'

import { useSnippets } from '../../../hooks/_useSnippets'
import { Button } from '../../UI/Button'

// Props for the delete snippet modal
export interface DeleteSnippetModalProps {
    isOpen?: boolean
    onClose?(): void
    snippetId: string
    snippetName: string
    onSuccess?(): void
}

// Modal for confirming snippet deletion
export const DeleteSnippetModal = ({ isOpen, onClose, snippetId, snippetName, onSuccess }: DeleteSnippetModalProps) => {
    const { deleteSnippet, isLoading } = useSnippets()

    // Handle the deletion and call onSuccess callback
    const handleDelete = useCallback(async () => {
        try {
            await deleteSnippet(snippetId)
            if (onSuccess) onSuccess()
            if (onClose) onClose()
        } catch (err) {
            // Error handling is done in useSnippets
        }
    }, [snippetId, deleteSnippet, onSuccess, onClose])

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={onClose!}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>
                <div className="fixed inset-0 z-10 w-screen overflow-y-visible">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md md:max-w-lg sm:px-3 sm:py-4 md:p-4">
                                <div className="flex">
                                    <ExclamationTriangleIcon className="h-10 w-10 text-rose-700 mr-2" />
                                    <div className="flex flex-col gap-1 w-full">
                                        <Dialog.Title as="h3" className="text-lg mt-2 font-semibold leading-6 text-gray-700 mb-2">
                                            Delete Snippet
                                        </Dialog.Title>
                                        <Dialog.Description>
                                            <p className="text-base text-gray-600">
                                                Are you sure you want to delete the snippet <span className="font-semibold">/{snippetName}</span>?
                                            </p>
                                            <p className="text-base text-gray-600">This action cannot be undone.</p>
                                        </Dialog.Description>
                                        <div className="flex mt-4 justify-end gap-2">
                                            <Button onClick={onClose} label="Cancel" intent="secondary" disabled={isLoading} />
                                            <Button onClick={handleDelete} label="Delete" intent="danger" isLoading={isLoading} />
                                        </div>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}
